// ABOUTME: "Next Responsibility" mini card for the All Kids dashboard tab — shows the first
// ABOUTME: incomplete chore list, with a grace period before it swaps or a new list appears (issue #1916)

import React from 'react';
import { Box, Paper, Typography, SxProps, Theme } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ChoreListPeriodStatus } from '../types/api';
import { isChoreListApproved } from '../utils/choreListUtils';
import { useDelayedChoreListView } from '../hooks/useDelayedChoreListView';
import { useFrozenNextChoreList } from '../hooks/useFrozenNextChoreList';

interface NextResponsibilityCardProps {
  choreStatuses: ChoreListPeriodStatus[];
  childId: number;
  onChoreItemToggle: (childId: number, listId: number, itemId: number, isComplete: boolean) => void;
  sx: SxProps<Theme>;
}

const NextResponsibilityCard: React.FC<NextResponsibilityCardProps> = ({
  choreStatuses,
  childId,
  onChoreItemToggle,
  sx,
}) => {
  const theme = useTheme();
  // Newly-created pinned lists wait out a grace period before they can become "next", and the
  // card keeps showing a just-completed list for the same period before swapping (issue #1916).
  const delayedChoreStatuses = useDelayedChoreListView(choreStatuses);
  const nextChoreList = useFrozenNextChoreList(delayedChoreStatuses);
  const isNextChoreListLocked = nextChoreList ? isChoreListApproved(nextChoreList) : false;
  const completedListsToday = delayedChoreStatuses.filter(list => list.isComplete).length;
  const totalListsToday = delayedChoreStatuses.length;

  return (
    <Paper elevation={1} sx={{ ...sx, p: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
        <Typography sx={{ fontSize: '12px', fontWeight: 800, color: 'text.secondary' }}>
          NEXT RESPONSIBILITY
        </Typography>
        {nextChoreList && (
          <Typography sx={{ ml: 'auto', fontSize: '11px', fontWeight: 800, color: 'primary.main' }}>
            {nextChoreList.completedItems}/{nextChoreList.totalItems}
          </Typography>
        )}
      </Box>
      {nextChoreList ? (
        <>
          <Typography sx={{ fontSize: '13px', fontWeight: 700, mb: 1 }}>{nextChoreList.title}</Typography>
          {nextChoreList.items.map(item => (
            <Box
              key={item.id}
              role="checkbox"
              aria-checked={item.isCompleted}
              aria-label={item.title}
              tabIndex={isNextChoreListLocked ? undefined : 0}
              onClick={isNextChoreListLocked ? undefined : () => onChoreItemToggle(childId, nextChoreList.id, item.id, item.isCompleted)}
              onKeyDown={isNextChoreListLocked ? undefined : (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onChoreItemToggle(childId, nextChoreList.id, item.id, item.isCompleted);
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontSize: '12.5px',
                mb: 0.75,
                color: item.isCompleted ? 'text.secondary' : 'text.primary',
                textDecoration: item.isCompleted ? 'line-through' : 'none',
                cursor: isNextChoreListLocked ? 'default' : 'pointer',
                '&:hover': isNextChoreListLocked ? {} : { textDecoration: item.isCompleted ? 'line-through' : 'underline' },
                '&:focus-visible': isNextChoreListLocked ? {} : { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: '2px', borderRadius: '4px' },
              }}
            >
              <Box
                sx={{
                  width: 15,
                  height: 15,
                  borderRadius: '5px',
                  border: '1.5px solid',
                  borderColor: 'text.disabled',
                  bgcolor: item.isCompleted ? 'primary.main' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  color: 'common.white',
                  flexShrink: 0,
                }}
              >
                {item.isCompleted ? '✓' : ''}
              </Box>
              {item.title}
            </Box>
          ))}
          <Typography sx={{ fontSize: '11px', color: 'text.secondary', mt: 1 }}>
            {completedListsToday} of {totalListsToday} lists pau today · this is the first list still open
          </Typography>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">No open lists</Typography>
      )}
    </Paper>
  );
};

export default NextResponsibilityCard;
